/**
 * Workspaces Data View - 워크스페이스별 데이터 분석
 *
 * - status/main: Cloud Storage 스캔 결과 (기존 방식)
 * - status/storage: BigQuery 집계 결과 (신규 방식)
 * 두 소스를 나란히 표시하여 비교.
 */

import { Trans, t } from '@lingui/macro';
import { Building2, MessageSquare, Users, HardDrive, RefreshCw, Loader2, AlertTriangle, Clock, Database, ScanLine } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Fire, getFunc } from '../../services/FireClient';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { IconHash } from '../components/FigmaIcons';

interface WorkspaceData {
    id: string;
    name: string;
    tier: string;
    // status/main — Cloud Storage scan
    scanFilesMB: number;
    scanScreenRecordingsMB: number;
    scanAiTrainingDocsMB: number;
    scanLastUpdated?: Date;
    // status/storage — BigQuery aggregation
    bqRoomCount: number;
    bqMemberCount: number;
    bqMessageCount: number;
    bqTotalUsedBytes: number;
    bqFileBytes: number;
    bqDocBytes: number;
    bqFileCount: number;
    bqLastUpdated?: Date;
}

const DevAdminWorkspaces: React.FC = () => {
    const [workspaces, setWorkspaces] = useState<WorkspaceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [recalculating, setRecalculating] = useState<string | null>(null);

    useEffect(() => {
        loadWorkspaces();
    }, []);

    const loadWorkspaces = async () => {
        setLoading(true);
        try {
            const store = Fire.store;
            const wsSnap = await getDocs(collection(store, 'workspaces'));

            const results: WorkspaceData[] = [];

            for (const wsDoc of wsSnap.docs) {
                const ws = wsDoc.data();

                // Read status/main (Cloud Storage scan)
                let scanFilesMB = 0, scanScreenRecordingsMB = 0, scanAiTrainingDocsMB = 0;
                let scanLastUpdated: Date | undefined;
                try {
                    const mainDoc = await getDoc(
                        doc(store, 'workspaces', wsDoc.id, 'status', 'main')
                    );
                    if (mainDoc.exists()) {
                        const d = mainDoc.data();
                        scanFilesMB = d.filesMB || 0;
                        scanScreenRecordingsMB = d.screenRecordingsMB || 0;
                        scanAiTrainingDocsMB = d.aiTrainingDocsMB || 0;
                        scanLastUpdated = d.lastUpdated?.toDate?.();
                    }
                } catch (e) {
                    console.error('Failed to load status/main for', wsDoc.id);
                }

                // Read status/storage (BigQuery aggregation)
                let bqRoomCount = 0, bqMemberCount = 0, bqMessageCount = 0;
                let bqTotalUsedBytes = 0, bqFileBytes = 0, bqDocBytes = 0, bqFileCount = 0;
                let bqLastUpdated: Date | undefined;
                try {
                    const storageDoc = await getDoc(
                        doc(store, 'workspaces', wsDoc.id, 'status', 'storage')
                    );
                    if (storageDoc.exists()) {
                        const d = storageDoc.data();
                        bqRoomCount = d.roomCount || 0;
                        bqMemberCount = d.memberCount || 0;
                        bqMessageCount = d.messageCount || 0;
                        bqTotalUsedBytes = d.totalUsedBytes || 0;
                        bqFileBytes = d.fileBytes || 0;
                        bqDocBytes = d.docBytes || 0;
                        bqFileCount = d.fileCount || 0;
                        bqLastUpdated = d.lastUpdated?.toDate?.();
                    }
                } catch (e) {
                    console.error('Failed to load status/storage for', wsDoc.id);
                }

                results.push({
                    id: wsDoc.id,
                    name: ws.name || 'Unknown',
                    tier: ws.tier || 'FREE',
                    scanFilesMB,
                    scanScreenRecordingsMB,
                    scanAiTrainingDocsMB,
                    scanLastUpdated,
                    bqRoomCount,
                    bqMemberCount,
                    bqMessageCount,
                    bqTotalUsedBytes,
                    bqFileBytes,
                    bqDocBytes,
                    bqFileCount,
                    bqLastUpdated,
                });
            }

            // Sort by BigQuery total → fallback to scan total
            results.sort((a, b) => {
                const bqA = a.bqTotalUsedBytes;
                const bqB = b.bqTotalUsedBytes;
                if (bqA !== bqB) return bqB - bqA;
                const scanA = a.scanFilesMB + a.scanScreenRecordingsMB + a.scanAiTrainingDocsMB;
                const scanB = b.scanFilesMB + b.scanScreenRecordingsMB + b.scanAiTrainingDocsMB;
                return scanB - scanA;
            });

            setWorkspaces(results);
        } catch (error) {
            console.error('Failed to load workspaces:', error);
        } finally {
            setLoading(false);
        }
    };

    const recalculateStorage = async (wsId: string) => {
        setRecalculating(wsId);
        try {
            const updateStatus = getFunc('updateWorkspaceStatusManual');
            await updateStatus({ workspaceId: wsId });
            await loadWorkspaces();
        } catch (error) {
            console.error('Failed to recalculate:', error);
            alert('재계산 실패: Cloud Function이 배포되어 있지 않거나 권한이 없습니다.');
        } finally {
            setRecalculating(null);
        }
    };

    const formatMB = (mb: number) => {
        if (mb >= 1000) return `${(mb / 1000).toFixed(1)} GB`;
        return `${mb.toFixed(1)} MB`;
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '—';
        const gb = bytes / (1024 * 1024 * 1024);
        if (gb >= 1) return `${gb.toFixed(2)} GB`;
        const mb = bytes / (1024 * 1024);
        if (mb >= 1) return `${mb.toFixed(1)} MB`;
        const kb = bytes / 1024;
        return `${kb.toFixed(0)} KB`;
    };

    const filteredWorkspaces = workspaces.filter(ws =>
        ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ws.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            <Trans>Workspaces Data</Trans>
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Storage usage and data integrity check</Trans>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Input
                            placeholder={t`Search workspaces...`}
                            className="w-64"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Button onClick={loadWorkspaces} variant="outline" size="sm">
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Summary */}
                        <div className="flex items-center gap-4 mb-4">
                            <Badge variant="outline">
                                Total: {workspaces.length} workspaces
                            </Badge>
                            <Badge variant="outline" className="gap-1">
                                <Database className="w-3 h-3" />
                                BQ 데이터: {workspaces.filter(ws => ws.bqLastUpdated).length}개
                            </Badge>
                        </div>

                        {/* Workspace List */}
                        {filteredWorkspaces.map((ws) => {
                            const scanTotalMB = ws.scanFilesMB + ws.scanScreenRecordingsMB + ws.scanAiTrainingDocsMB;
                            const bqTotalMB = ws.bqTotalUsedBytes / (1024 * 1024);
                            const hasBoth = scanTotalMB > 0 && ws.bqTotalUsedBytes > 0;
                            const discrepancyPct = hasBoth
                                ? Math.abs(scanTotalMB - bqTotalMB) / scanTotalMB * 100
                                : 0;
                            const hasDiscrepancy = discrepancyPct > 10;

                            return (
                                <Card key={ws.id} className={`overflow-hidden ${hasDiscrepancy ? 'ring-2 ring-amber-400' : ''}`}>
                                    <CardContent className="p-0">
                                        <div className="flex items-center">
                                            <div className="p-4 flex-1">
                                                {/* Title */}
                                                <div className="flex items-center gap-3 mb-1">
                                                    <Building2 className="w-5 h-5 text-slate-400" />
                                                    <span className="font-semibold text-slate-900">{ws.name}</span>
                                                    <Badge variant="secondary">{ws.tier}</Badge>
                                                    {hasDiscrepancy && (
                                                        <Badge variant="destructive" className="gap-1">
                                                            <AlertTriangle className="w-3 h-3" />
                                                            {discrepancyPct.toFixed(0)}% 차이
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500 mb-3">{ws.id}</div>

                                                {/* BigQuery counts */}
                                                <div className="flex items-center gap-5 text-sm mb-3">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <MessageSquare className="w-3.5 h-3.5 text-green-500" />
                                                        <span className="font-medium">{ws.bqRoomCount.toLocaleString()}</span>
                                                        <span className="text-slate-400">rooms</span>
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Users className="w-3.5 h-3.5 text-blue-500" />
                                                        <span className="font-medium">{ws.bqMemberCount.toLocaleString()}</span>
                                                        <span className="text-slate-400">members</span>
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <IconHash className="w-3.5 h-3.5 text-amber-500" />
                                                        <span className="font-medium">{ws.bqMessageCount.toLocaleString()}</span>
                                                        <span className="text-slate-400">messages</span>
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <HardDrive className="w-3.5 h-3.5 text-slate-400" />
                                                        <span className="font-medium">{ws.bqFileCount.toLocaleString()}</span>
                                                        <span className="text-slate-400">files</span>
                                                    </span>
                                                </div>

                                                {/* Storage comparison */}
                                                <div className="flex items-center gap-6 text-sm">
                                                    <div className="flex items-center gap-2" title="Cloud Storage 스캔 (status/main)">
                                                        <ScanLine className="w-4 h-4 text-purple-500" />
                                                        <span className="text-slate-500">스캔:</span>
                                                        <span className="font-medium">{formatMB(scanTotalMB)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2" title="BigQuery 집계 (status/storage)">
                                                        <Database className="w-4 h-4 text-cyan-500" />
                                                        <span className="text-slate-500">BQ:</span>
                                                        <span className="font-medium">{formatBytes(ws.bqTotalUsedBytes)}</span>
                                                        {ws.bqTotalUsedBytes > 0 && (
                                                            <span className="text-xs text-slate-400">
                                                                (파일 {formatBytes(ws.bqFileBytes)} + 문서 {formatBytes(ws.bqDocBytes)})
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Timestamps */}
                                                <div className="flex items-center gap-4 mt-2">
                                                    {ws.scanLastUpdated && (
                                                        <span className="text-xs text-slate-400 flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            스캔: {ws.scanLastUpdated.toLocaleString()}
                                                        </span>
                                                    )}
                                                    {ws.bqLastUpdated && (
                                                        <span className="text-xs text-cyan-400 flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            BQ: {ws.bqLastUpdated.toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="p-4 border-l border-slate-100">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => recalculateStorage(ws.id)}
                                                    disabled={recalculating === ws.id}
                                                >
                                                    {recalculating === ws.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <RefreshCw className="w-4 h-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminWorkspaces;
